/**
 * Futuras importações (F3c) — porte de src/pages/FuturasImportacoes do app atual,
 * mesmas regras (packages/core/services/futurasImportacoesService): planejamento
 * manual, colunas dinâmicas, nenhuma célula calculada; "Peças no estoque" é o
 * saldo REAL (não editável). Grava ao sair do campo (como no app atual).
 */
import { createEffect, createMemo, createResource, createSignal, For, on, onCleanup, Show } from 'solid-js'
import { Portal } from 'solid-js/web'
import { Button } from '@ui/components/Button'
import type { FutimpProdutoOpcao, FutimpTabela } from '@shared/types'
import { api, versaoDados } from '../nucleo/cliente'
import { mensagemErro } from '../lib/erro'
import { confirmar, Erro, num, Vazio } from '../ui/kit'

// "22.038,42" | "22038.42" | "22038,42" → número (null se vazio/inválido) — igual ao app atual.
function lerReais(s: string): number | null {
  const t = s.trim()
  if (!t) return null
  const n = Number(t.replace(/\s|R\$/g, '').replace(/\.(?=\d{3}(\D|$))/g, '').replace(',', '.'))
  return Number.isNaN(n) ? null : n
}
const CAMPO = 'w-full bg-transparent px-2 py-1 text-sm text-ink outline-none focus:bg-hover rounded'

export function FuturasImportacoes(props: { podeEditar: boolean }) {
  const [produtos] = createResource(() => versaoDados(), () => api.futurasImportacoes.produtos())
  const [tabela, setTabela] = createSignal<FutimpTabela | null>(null)
  const [valores, setValores] = createSignal<Record<number, string>>({})
  const [erro, setErro] = createSignal<string | null>(null)
  let editando = false // não recarrega por cima de quem está digitando

  const carregar = async () => {
    try {
      setErro(null)
      const t = await api.futurasImportacoes.get()
      setTabela(t)
      setValores(Object.fromEntries(t.linhas.map((l) => [l.id, l.valorReais != null ? num(l.valorReais) : ''])))
    } catch (e) {
      setErro(mensagemErro(e))
    }
  }
  createEffect(on(versaoDados, () => !editando && void carregar()))

  const comErro = async (fn: () => Promise<unknown>, recarregar = false) => {
    try {
      setErro(null)
      await fn()
      if (recarregar) await carregar()
    } catch (e) {
      setErro(mensagemErro(e))
      void carregar()
    }
  }
  const mudarCelulaLocal = (linhaId: number, colunaId: number, valor: string) => {
    const t = tabela()
    if (t) setTabela({ ...t, linhas: t.linhas.map((l) => (l.id === linhaId ? { ...l, celulas: { ...l.celulas, [colunaId]: valor } } : l)) })
  }
  const removerColuna = async (id: number, nome: string) => {
    if (await confirmar('Remover coluna?', `Remover a coluna "${nome}" e todos os seus valores?`, { rotulo: 'Remover', perigo: true }))
      void comErro(() => api.futurasImportacoes.removerColuna(id), true)
  }
  const removerLinha = async (id: number) => {
    if (await confirmar('Remover linha?', 'Remover esta linha?', { rotulo: 'Remover', perigo: true })) void comErro(() => api.futurasImportacoes.removerLinha(id), true)
  }

  // reordenar linhas por arrastar (persiste só a ordem)
  let arrastando: number | null = null
  const [sobre, setSobre] = createSignal<number | null>(null)
  const soltar = (destino: number) => {
    const de = arrastando
    arrastando = null
    setSobre(null)
    const t = tabela()
    if (de === null || de === destino || !t) return
    const linhas = [...t.linhas]
    const [movida] = linhas.splice(de, 1)
    linhas.splice(destino, 0, movida)
    setTabela({ ...t, linhas })
    void comErro(() => api.futurasImportacoes.reordenarLinhas(linhas.map((l) => l.id)))
  }

  const th = 'border-b border-r border-edge bg-panel px-2 py-2 text-left text-xs font-medium text-ink-subtle'
  const td = 'border-b border-r border-edge-muted align-top'

  return (
    <div class="flex flex-col gap-5 p-4 sm:p-6 min-h-0">
      <div class="flex flex-wrap items-start gap-3">
        <div class="mr-auto">
          <h1 class="text-xl font-semibold text-ink">Futuras importações</h1>
          <p class="text-xs text-ink-subtle">Planejamento manual. Nenhuma célula tem cálculo — todos os valores são digitados por você.</p>
        </div>
        <Show when={props.podeEditar}>
          <Button variant="ghost" size="sm" onClick={() => void comErro(() => api.futurasImportacoes.addColuna('Nova coluna'), true)}>+ Coluna</Button>
          <Button variant="accent" size="sm" onClick={() => void comErro(() => api.futurasImportacoes.addLinha(), true)}>+ Linha</Button>
        </Show>
      </div>
      <Erro mensagem={erro()} />
      <Show when={tabela()} fallback={<Vazio titulo="Carregando…" />}>
        {(t) => (
          <Show when={t().linhas.length > 0} fallback={<Vazio titulo="Tabela vazia" texto='Clique em "+ Linha" para começar e "+ Coluna" para adicionar colunas de planejamento.' />}>
            <div class="ped-rolagem overflow-auto rounded-xl border border-edge bg-panel">
              <table class="min-w-full border-separate border-spacing-0 text-sm">
                <thead>
                  <tr>
                    <th class={`${th} sticky left-0 z-10 min-w-[15rem]`}>Produto</th>
                    <th class={`${th} min-w-[8rem] text-right`}>
                      Peças no estoque
                      <div class="text-[10px] font-normal text-ink-placeholder">saldo real do estoque · não editável</div>
                    </th>
                    <th class={`${th} min-w-[9rem] text-right`}>Valor (R$)</th>
                    <For each={t().colunas}>
                      {(c) => (
                        <th class={`${th} min-w-[9rem]`}>
                          <div class="flex items-center gap-1">
                            <input
                              class="w-full bg-transparent font-medium text-ink outline-none"
                              value={c.nome}
                              disabled={!props.podeEditar}
                              onFocus={() => (editando = true)}
                              onBlur={(e) => {
                                editando = false
                                const nome = e.currentTarget.value.trim() || c.nome
                                if (nome !== c.nome) void comErro(() => api.futurasImportacoes.renomearColuna(c.id, nome), true)
                              }}
                            />
                            <Show when={props.podeEditar}>
                              <button type="button" title="Remover coluna" class="shrink-0 rounded px-1 text-ink-placeholder hover:text-failure" onClick={() => void removerColuna(c.id, c.nome)}>×</button>
                            </Show>
                          </div>
                        </th>
                      )}
                    </For>
                    <th class={`${th} w-10`} />
                  </tr>
                </thead>
                <tbody>
                  <For each={t().linhas}>
                    {(l, idx) => (
                      <tr
                        class="hover:bg-hover"
                        classList={{ 'bg-accent/10': sobre() === idx() && arrastando !== null }}
                        onDragOver={(e) => {
                          if (arrastando === null) return
                          e.preventDefault()
                          setSobre(idx())
                        }}
                        onDrop={() => soltar(idx())}
                      >
                        <td class={`${td} sticky left-0 z-10 bg-panel`}>
                          <div class="flex items-center gap-1">
                            <Show when={props.podeEditar}>
                              <span
                                draggable="true"
                                title="Arrastar para reordenar"
                                class="cursor-grab select-none px-1 text-ink-placeholder hover:text-ink"
                                onDragStart={(e) => ((arrastando = idx()), e.dataTransfer?.setData('text/plain', String(l.id)))}
                                onDragEnd={() => ((arrastando = null), setSobre(null))}
                              >
                                ⠿
                              </span>
                            </Show>
                            <div class="min-w-0 flex-1">
                              <BuscaProduto
                                produtos={produtos() ?? []}
                                codigo={l.modeloCodigo}
                                desabilitado={!props.podeEditar}
                                aoEscolher={(p) => {
                                  // mostra o saldo REAL na hora; grava só o produto escolhido
                                  const tt = tabela()!
                                  setTabela({
                                    ...tt,
                                    linhas: tt.linhas.map((x) => (x.id === l.id ? { ...x, modeloId: p?.id ?? null, modeloCodigo: p?.codigoModelo ?? null, saldoEstoque: p ? p.saldoAtual : null } : x)),
                                  })
                                  void comErro(() => api.futurasImportacoes.atualizarLinha(l.id, { modeloId: p ? p.id : null }))
                                }}
                              />
                            </div>
                          </div>
                        </td>
                        <td class={`${td} px-2 py-1 text-right font-semibold tabular-nums text-ink`}>
                          {l.saldoEstoque != null ? num(l.saldoEstoque) : <span class="text-ink-placeholder">—</span>}
                        </td>
                        <td class={td}>
                          <div class="flex items-center">
                            <span class="pl-2 text-xs text-ink-subtle">R$</span>
                            <input
                              class={`${CAMPO} text-right tabular-nums`}
                              inputMode="decimal"
                              disabled={!props.podeEditar}
                              value={valores()[l.id] ?? ''}
                              onFocus={() => (editando = true)}
                              onInput={(e) => setValores({ ...valores(), [l.id]: e.currentTarget.value })}
                              onBlur={(e) => {
                                editando = false
                                const v = lerReais(e.currentTarget.value)
                                setValores({ ...valores(), [l.id]: v != null ? num(v) : '' })
                                if (v !== l.valorReais) void comErro(() => api.futurasImportacoes.atualizarLinha(l.id, { valorReais: v }))
                              }}
                            />
                          </div>
                        </td>
                        <For each={t().colunas}>
                          {(c) => (
                            <td class={td}>
                              <input
                                class={CAMPO}
                                disabled={!props.podeEditar}
                                value={l.celulas[c.id] ?? ''}
                                onFocus={() => (editando = true)}
                                onBlur={(e) => {
                                  editando = false
                                  const valor = e.currentTarget.value
                                  if (valor !== (l.celulas[c.id] ?? '')) {
                                    mudarCelulaLocal(l.id, c.id, valor)
                                    void comErro(() => api.futurasImportacoes.setCelula(l.id, c.id, valor))
                                  }
                                }}
                              />
                            </td>
                          )}
                        </For>
                        <td class={`${td} text-center`}>
                          <Show when={props.podeEditar}>
                            <button type="button" title="Remover linha" class="rounded px-1.5 text-ink-placeholder hover:text-failure" onClick={() => void removerLinha(l.id)}>×</button>
                          </Show>
                        </td>
                      </tr>
                    )}
                  </For>
                </tbody>
              </table>
            </div>
          </Show>
        )}
      </Show>
    </div>
  )
}

/** Escolher produto por busca (código ou nome); a lista abre fora da tabela para não ser cortada. */
function BuscaProduto(props: { produtos: FutimpProdutoOpcao[]; codigo: string | null; desabilitado?: boolean; aoEscolher: (p: FutimpProdutoOpcao | null) => void }) {
  let campo: HTMLInputElement | undefined
  const [aberto, setAberto] = createSignal(false)
  const [q, setQ] = createSignal('')
  const [realce, setRealce] = createSignal(0)
  const [pos, setPos] = createSignal<{ left: number; top: number; width: number } | null>(null)
  const achados = createMemo(() => {
    const t = q().trim().toLowerCase()
    return (t ? props.produtos.filter((p) => p.codigoModelo.toLowerCase().includes(t) || p.produtoNome.toLowerCase().includes(t)) : props.produtos).slice(0, 40)
  })
  const posicionar = () => {
    if (!campo) return
    const r = campo.getBoundingClientRect()
    setPos({ left: r.left, top: r.bottom + 2, width: Math.max(r.width, 280) })
  }
  createEffect(() => {
    if (!aberto()) return
    posicionar()
    const fora = (e: MouseEvent) => {
      const alvo = e.target as Node
      if (!campo?.contains(alvo) && !(alvo as HTMLElement).closest?.('[data-busca-produto]')) setAberto(false)
    }
    window.addEventListener('scroll', posicionar, true)
    window.addEventListener('resize', posicionar)
    document.addEventListener('mousedown', fora)
    onCleanup(() => {
      window.removeEventListener('scroll', posicionar, true)
      window.removeEventListener('resize', posicionar)
      document.removeEventListener('mousedown', fora)
    })
  })
  const escolher = (p: FutimpProdutoOpcao | null) => {
    props.aoEscolher(p)
    setAberto(false)
    setQ('')
    campo?.blur()
  }
  return (
    <>
      <input
        ref={campo}
        class={CAMPO}
        disabled={props.desabilitado}
        placeholder={props.codigo ?? '— buscar produto —'}
        value={aberto() ? q() : (props.codigo ?? '')}
        onFocus={() => (setQ(''), setRealce(0), setAberto(true))}
        onInput={(e) => (setQ(e.currentTarget.value), setAberto(true), setRealce(0))}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') (e.preventDefault(), setAberto(true), setRealce(Math.min(realce() + 1, achados().length - 1)))
          else if (e.key === 'ArrowUp') (e.preventDefault(), setRealce(Math.max(realce() - 1, 0)))
          else if (e.key === 'Enter') {
            e.preventDefault()
            if (achados()[realce()]) escolher(achados()[realce()])
          } else if (e.key === 'Escape') setAberto(false)
        }}
      />
      <Show when={aberto() && pos()}>
        <Portal>
          <div
            data-busca-produto
            style={{ position: 'fixed', left: `${pos()!.left}px`, top: `${pos()!.top}px`, width: `${pos()!.width}px`, 'z-index': 60 }}
            class="max-h-72 overflow-auto rounded-md border border-edge bg-panel shadow-xl"
          >
            <Show when={props.codigo}>
              <button type="button" class="block w-full border-b border-edge px-3 py-1.5 text-left text-xs text-ink-subtle hover:bg-hover" onMouseDown={(e) => (e.preventDefault(), escolher(null))}>
                limpar seleção
              </button>
            </Show>
            <Show when={achados().length > 0} fallback={<div class="px-3 py-2 text-sm text-ink-subtle">Nenhum produto encontrado.</div>}>
              <For each={achados()}>
                {(p, i) => (
                  <button
                    type="button"
                    class="block w-full px-3 py-1.5 text-left text-sm text-ink hover:bg-hover"
                    classList={{ 'bg-hover': i() === realce() }}
                    onMouseEnter={() => setRealce(i())}
                    onMouseDown={(e) => (e.preventDefault(), escolher(p))}
                  >
                    <span class="font-medium">{p.codigoModelo}</span>
                    <span class="ml-2 text-xs text-ink-subtle">{p.produtoNome} · saldo {num(p.saldoAtual)}</span>
                  </button>
                )}
              </For>
            </Show>
          </div>
        </Portal>
      </Show>
    </>
  )
}
